import React, { useState, useEffect } from "react";
import api, { apiErr } from "../lib/api";
import { MODALIDADES } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { PackageOpen, Repeat, Loader2 } from "lucide-react";
import { toast } from "sonner";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

export function ResolverIncidenciaModal({ envio, onClose, onDone, canAlmacenar = false }) {
  const [opcion, setOpcion] = useState(null);
  const [form, setForm] = useState({ destinatario: "", whatsapp: "", ciudad: "", valor_producto: "", costo_envio: "", modalidad: "cod", notas: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (envio) {
      setOpcion(null);
      setForm({ destinatario: "", whatsapp: "", ciudad: envio.ciudad || "", valor_producto: envio.valor_producto || "", costo_envio: envio.costo_envio || "", modalidad: envio.modalidad || "cod", notas: "" });
    }
  }, [envio]);

  if (!envio) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const almacenar = async () => {
    setSaving(true);
    try {
      await api.post(`/envios/${envio.id}/resolver-incidencia`, { accion: "almacenar" });
      toast.success(`${envio.code} almacenado en sucursal de destino`);
      onDone?.(); onClose();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  const reasignar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/envios/${envio.id}/reasignar`, {
        ...form, valor_producto: Number(form.valor_producto) || 0, costo_envio: Number(form.costo_envio) || 0,
      });
      if (data.nueva_boleta) toast.success(`Nueva boleta ${data.nueva_boleta} creada en sucursal de destino`);
      else toast.success("Solicitud enviada. Debe aprobarla un administrador o el encargado de la sucursal.");
      onDone?.(); onClose();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!envio} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Resolver incidencia · {envio.code}</DialogTitle>
          <DialogDescription>{envio.incidente?.motivo_label || "Incidencia"}{envio.incidente?.reagendar_fecha ? ` · reprogramado: ${envio.incidente.reagendar_fecha}` : ""}</DialogDescription>
        </DialogHeader>
        {opcion === null && (
          <div className="space-y-3">
            {canAlmacenar && (
              <button onClick={() => setOpcion("almacenar")} data-testid="resolver-almacenar-opcion"
                className="w-full text-left rounded-xl border border-violet-200 bg-violet-50/60 hover:bg-violet-50 p-4 flex items-start gap-3">
                <PackageOpen className="h-5 w-5 text-violet-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-[#0A1F44]">Almacenar producto</div>
                  <div className="text-xs text-[#64748B]">Queda guardado en la sucursal de destino hasta nueva instrucción.</div>
                </div>
              </button>
            )}
            <button onClick={() => setOpcion("reasignar")} data-testid="resolver-reasignar-opcion"
              className="w-full text-left rounded-xl border border-teal-200 bg-teal-50/60 hover:bg-teal-50 p-4 flex items-start gap-3">
              <Repeat className="h-5 w-5 text-teal-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-[#0A1F44]">Reasignar producto</div>
                <div className="text-xs text-[#64748B]">El remitente encontró un nuevo comprador: se genera una nueva boleta con estado "En sucursal destino".{canAlmacenar ? "" : " Tu solicitud será aprobada por un administrador o el encargado de la sucursal."}</div>
              </div>
            </button>
          </div>
        )}
        {opcion === "almacenar" && (
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">El paquete <b>{envio.code}</b> quedará almacenado en la sucursal de destino ({envio.sucursal_destino}). Podrás reasignarlo después.</p>
            <button onClick={almacenar} disabled={saving} data-testid="resolver-almacenar-submit"
              className="w-full rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar almacenado
            </button>
            <button onClick={() => setOpcion(null)} className="w-full rounded-lg border border-slate-300 py-2.5 text-sm font-medium">Volver</button>
          </div>
        )}
        {opcion === "reasignar" && (
          <form onSubmit={reasignar} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Nuevo destinatario *</label>
              <input className={input} value={form.destinatario} onChange={set("destinatario")} required data-testid="reasignar-destinatario-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">WhatsApp *</label>
                <input className={input} value={form.whatsapp} onChange={set("whatsapp")} required placeholder="0981..." data-testid="reasignar-whatsapp-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Ciudad *</label>
                <input className={input} value={form.ciudad} onChange={set("ciudad")} required data-testid="reasignar-ciudad-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Valor producto (Gs.)</label>
                <input type="number" className={input} value={form.valor_producto} onChange={set("valor_producto")} data-testid="reasignar-valor-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Flete (Gs.)</label>
                <input type="number" className={input} value={form.costo_envio} onChange={set("costo_envio")} data-testid="reasignar-flete-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Modalidad de pago</label>
              <select className={input} value={form.modalidad} onChange={set("modalidad")} data-testid="reasignar-modalidad-select">
                {MODALIDADES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <button disabled={saving} data-testid="reasignar-submit"
              className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {canAlmacenar ? "Crear nueva boleta" : "Solicitar reasignación"}
            </button>
            <button type="button" onClick={() => setOpcion(null)} className="w-full rounded-lg border border-slate-300 py-2.5 text-sm font-medium">Volver</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
